import { Optional, Pipe, PipeTransform } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Mentionables } from '../interfaces/ui-quill-mention.interfaces';
import { UiQuillMentionContract } from '../services/ui-quill-mention.contract';

const DEBOUNCE_TIMER: number = 200;

/**
 * EnrichMentionPipe: This pipe adds additional data required by the mention blot to properly render it.
 * It recognizes the denotation character and fetches additional information based on it.
 */
@Pipe({
  name: 'enrichMention'
})
export class EnrichMentionPipe implements PipeTransform {
  constructor(@Optional() private mentionContract: UiQuillMentionContract) {}

  static missingThings: Set<string> = new Set<string>();
  static debounceTimeout: number = 0;
  static readonly periodicFetchSubject: Subject<void> = new Subject<void>();
  static readonly periodicFetchObserver: Observable<void> = EnrichMentionPipe.periodicFetchSubject.asObservable();

  // Set these from outside of the pipe, maybe from the host component!
  static inaccessiblePeople: Set<string> = new Set<string>();
  static inaccessibleThings: Set<string> = new Set<string>();

  fetchMissing() {
    // We wait for all the missingThings to be enlisted and populate them with a single fetch
    if (EnrichMentionPipe.debounceTimeout) {
      clearTimeout(EnrichMentionPipe.debounceTimeout);
    }
    // We fetch in bulks so used debounce. Used setTimeout() because, we don't want to pollute
    // this simple blot by introducing complex libraries
    EnrichMentionPipe.debounceTimeout = window.setTimeout(() => {
      this.mentionContract
        .fetchSpecificElements(
          Array.from(EnrichMentionPipe.missingThings).filter((things) => !EnrichMentionPipe.inaccessibleThings.has(things))
        )
        .then(() => {
          EnrichMentionPipe.periodicFetchSubject.next(null);
        });
      EnrichMentionPipe.missingThings = new Set<string>();
    }, DEBOUNCE_TIMER);
  }

  transform(value: string): Promise<string> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<string>(async (resolve, reject) => {
      if (!this.mentionContract) {
        console.warn(
          'Mention Contract is not defined. Please check if it needs to be provided. Rejecting is not an option, so resolved same value.'
        );
        resolve(value);
        return;
      }
      if (typeof value !== 'string') {
        console.warn('Expecting string value, but received non string value!');
        resolve(value);
        return;
      }
      const ops = JSON.parse(value);

      /* Bulk request for the mentioned users and fetch  user details */
      const mentionedUsers: Map<string, Mentionables> = await this.getMentionedUsers(value);

      // Wrapped these variabled in an object so that they can be referenced
      const mentionStats = { totalThingsMentioned: 0, reachedEnd: false };

      if (ops?.ops?.length) {
        // Incase we have ops, we iterate them to process the mentions

        for (const op of ops.ops) {
          if (op?.insert?.mention) {
            // If it is a mention, we treat each denotationChars differently
            switch (op.insert.mention.denotationChar) {
              case '@':
                // allPeople are loaded by default so we simply pick one with the id
                const userId = op.insert.mention.id;
                const user = mentionedUsers.get(userId);

                if (!user) {
                  /**
                   * At this point, we should at least have some placeholder Mentionable for the inaccessible user.
                   * If not, then we fallback to default one as final fallback
                   */
                  op.insert.mention = {
                    id: userId,
                    value: 'Anonymous User',
                    user: { status: null },
                    denotationChar: '@'
                  };
                } else {
                  op.insert.mention = user;
                }
                break;
              case '#':
                // a thing mention however is async and fetch only when needed
                mentionStats.totalThingsMentioned++;
                this.mentionContract.allThings.then((things) => {
                  if (!things.has(op.insert.mention.id) && !EnrichMentionPipe.inaccessibleThings.has(op.insert.mention.id)) {
                    // If we don't have the thing being mentioned, we add them to the missing list.
                    EnrichMentionPipe.missingThings.add(op.insert.mention.id);
                    this.fetchMissing();

                    // We periodically look if the missed things are fetched and enrich them.
                    const openSubscription = EnrichMentionPipe.periodicFetchObserver.subscribe(() => {
                      if (EnrichMentionPipe.inaccessibleThings.has(op.insert.mention.id)) {
                        EnrichMentionPipe.missingThings.delete(op.insert.mention.id);
                        if (this.mentionContract.addInaccessibleUris) {
                          // This is an optional method
                          this.mentionContract.addInaccessibleUris('Things', [op.insert.mention.id]);
                        }
                      }
                      this.mentionContract.allThings.then((newThings) => {
                        if (newThings.has(op.insert.mention.id)) {
                          this.setMentionData(newThings.get(op.insert.mention.id), {
                            openSubscription,
                            op,
                            mentionStats,
                            ops,
                            resolve
                          });
                        }
                      });
                    });
                    return;
                  }
                  this.setMentionData(things.get(op.insert.mention.id), {
                    op,
                    mentionStats,
                    ops,
                    resolve
                  });
                });
                break;
            }
          }
        }
      }
      mentionStats.reachedEnd = true;
      if (mentionStats.totalThingsMentioned === 0) {
        // If we don't have missing things and reached the end of the ops, we resolve
        resolve(JSON.stringify(ops));
      }
    });
  }

  /**
   * Collection all user id from the RTE content and bulk request for the user
   * detail and provide users map
   *
   */
  async getMentionedUsers(value: string): Promise<Map<string, Mentionables>> {
    const mappedUsers: Map<string, Mentionables> = new Map();
    try {
      /* Convert RTE content to json  */
      const ops = JSON.parse(value).ops;

      /* Extract mentioned users uri from RTE json data  */
      const usersUri = ops.reduce((uris, op) => {
        const { denotationChar, id } = op?.insert?.mention || {};
        if (denotationChar === '@' && id) {
          uris.push(id);
        }
        return uris;
      }, []);

      /* If users aren't mentioned in RTE field then we return the empty user map */
      if (!usersUri.length) {
        return mappedUsers;
      }

      /* Remove the duplicate userUris */
      const uniqueUsersUri = [...new Set(usersUri)] as Array<string>;

      /* Fetch the user details */
      const usersDetails: Array<Mentionables> = await this.mentionContract.getUsersByUri(uniqueUsersUri);

      /* Map the mentionable user with id so it's easy to retrieve the user data without loop */
      return usersDetails.reduce((mapped: Map<string, Mentionables>, user: Mentionables) => {
        if (user?.id) {
          mapped.set(user.id, user);
        }
        return mapped;
      }, mappedUsers);
    } catch (err) {
      return mappedUsers;
    }
  }

  // Called internally when we have new Data which updates the context and resolves if no more missing data being fetched
  private setMentionData(mentionData: Mentionables, context: any) {
    // Unaccessible or found, so we no longer subscribe to the fetcher
    if (context.openSubscription) {
      context.openSubscription.unsubscribe();
    }

    context.op.insert.mention = mentionData;
    if (--context.mentionStats.totalThingsMentioned === 0 && context.mentionStats.reachedEnd) {
      // If we have resolved every missing things and reached the end of the ops, we resolve
      context.resolve(JSON.stringify(context.ops));
    }
  }
}
